import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight } from "lucide-react";
import { getActiveAnnouncement } from "@/data/holidayAnnouncements";

const DISMISS_KEY = "holiday-banner-dismissed";
const DISMISS_HOURS = 24;

export function HolidayBanner() {
  const announcement = useMemo(() => getActiveAnnouncement(), []);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(DISMISS_KEY);
      if (raw) {
        const { id, until } = JSON.parse(raw);
        if (id === announcement.id && Date.now() < until) return;
      }
    } catch {
      // ignore
    }
    setVisible(true);
  }, [announcement.id]);

  const dismiss = () => {
    try {
      localStorage.setItem(
        DISMISS_KEY,
        JSON.stringify({
          id: announcement.id,
          until: Date.now() + DISMISS_HOURS * 60 * 60 * 1000,
        }),
      );
    } catch {
      // ignore
    }
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="w-full overflow-hidden"
          style={{
            backgroundColor: announcement.accentBg,
            color: announcement.accentFg,
          }}
        >
          <div className="container mx-auto px-4 py-2 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <span className="text-base shrink-0" aria-hidden>
                {announcement.emoji}
              </span>
              <p className="text-xs sm:text-sm font-medium truncate">
                {announcement.message}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Link
                to={announcement.ctaHref}
                className="hidden sm:inline-flex items-center gap-1 text-xs sm:text-sm font-semibold underline-offset-4 hover:underline"
              >
                {announcement.ctaLabel}
                <ArrowRight className="h-3 w-3" />
              </Link>
              <Link
                to={announcement.ctaHref}
                className="sm:hidden text-xs font-semibold underline"
                aria-label={announcement.ctaLabel}
              >
                Shop →
              </Link>
              <button
                type="button"
                onClick={dismiss}
                aria-label="Dismiss announcement"
                className="opacity-70 hover:opacity-100 transition-opacity"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
